<template>
  <div class="gp-page iabf-dancer-theme">
    <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
    <h1>Group pieces</h1>
    <p class="lede">
      Shared dances at the festival — join the ones you’d like to be in.
      Rehearsal times will show on the itinerary once they’re set.
    </p>
    <p v-if="error" class="err" role="alert">{{ error }}</p>
    <p v-if="loading" class="muted">Loading…</p>

    <article v-for="p in pieces" :key="p.id" class="gp-card">
      <h2>{{ p.title }}</h2>
      <p v-if="p.facultyName" class="muted">With {{ p.facultyName }}</p>
      <ul v-if="(p.rehearsals || []).length" class="gp-rehearsals">
        <li v-for="r in p.rehearsals" :key="r.id">
          {{ dayLabel(r.day) }} · {{ compactTimeLabel(r.startTime, r.endTime) }}
          <span v-if="r.room"> · {{ r.room }}</span>
        </li>
      </ul>
      <p v-else class="muted">Rehearsal time coming once we’re at the festival.</p>
      <p class="muted">{{ (p.allDancerIds || []).length }} dancer{{ (p.allDancerIds || []).length === 1 ? '' : 's' }} in this one.</p>
      <button
        v-if="isIn(p)"
        type="button"
        class="gp-btn is-on"
        :disabled="busy === p.id"
        @click="leave(p)"
      >You’re in — leave</button>
      <button
        v-else-if="p.openForRegistration !== false"
        type="button"
        class="gp-btn"
        :disabled="busy === p.id"
        @click="join(p)"
      >Join</button>
      <p v-else class="muted">Joining is closed for now.</p>
    </article>
    <p v-if="!loading && !pieces.length" class="muted">No group pieces yet — check back soon.</p>
  </div>
</template>

<script>
import { functions } from '@/firebase'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import {
  compactTimeLabel,
  dancerIsInGroupPiece,
  GROUP_PIECE_DAYS
} from '@/config/iabf-group-pieces'

export default {
  name: 'IABFGroupPieces',
  data () {
    return {
      loading: true,
      busy: '',
      error: '',
      pieces: []
    }
  },
  computed: {
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    year () {
      return Number(this.dancer.festivalYear) || IABF_DANCER_YEAR
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.reload()
  },
  methods: {
    compactTimeLabel,
    dayLabel (key) {
      const row = GROUP_PIECE_DAYS.find((d) => d.key === key)
      return row ? row.label : key
    },
    isIn (piece) {
      return dancerIsInGroupPiece(piece, this.dancer.id)
    },
    async reload () {
      this.loading = true
      this.error = ''
      try {
        this.pieces = await this.$store.dispatch('loadGroupPieces', { useAdminYear: false })
      } catch (e) {
        this.error = (e && e.message) || 'Could not load group pieces.'
      }
      this.loading = false
    },
    async join (piece) {
      this.busy = piece.id
      this.error = ''
      try {
        const callable = functions.httpsCallable('joinGroupPiece')
        await callable({ year: this.year, pieceId: piece.id })
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not join that piece.'
      }
      this.busy = ''
    },
    async leave (piece) {
      this.busy = piece.id
      this.error = ''
      try {
        const callable = functions.httpsCallable('leaveGroupPiece')
        await callable({ year: this.year, pieceId: piece.id })
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not update that.'
      }
      this.busy = ''
    }
  }
}
</script>

<style scoped>
.gp-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 96px;
}
.back-link { color: var(--iabf-ink-faint, #8a8792); text-decoration: none; font-size: 15px; }
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 32px;
  margin: 12px 0 8px;
}
.lede { font-size: 17px; color: var(--iabf-ink-muted, #5c5a63); line-height: 1.5; }
.muted { color: var(--iabf-ink-muted, #5c5a63); }
.err { color: var(--iabf-danger, #b54a4a); }
.gp-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 14px 16px;
  margin: 14px 0;
}
.gp-card h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0 0 6px;
}
.gp-rehearsals { margin: 8px 0; padding-left: 18px; }
.gp-btn {
  margin-top: 8px;
  border: 1.5px solid var(--iabf-primary, #8a957d);
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border-radius: 999px;
  padding: 8px 16px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.gp-btn.is-on {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
</style>
